import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash';
import { useDispatch } from 'react-redux';
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect';

import { setResourceVersions } from 'apps/web_forms/slices/formSlice';
import {
  fetchResources,
  setResourcesLoading,
} from 'apps/web_forms/slices/resourceSlice';
import useGlobalSelector from 'apps/web_forms/hooks/useGlobalSelector';

const areResourceVersionsEmpty = (resourceVersions) =>
  resourceVersions == null || !Object.keys(resourceVersions).length;

/**
 * Fetch into memory any resources needed for the given form, by cache if
 * possible or network request otherwise, so they are available to be used
 * @param form
 * @param {boolean} isDraft
 * @param draftMetadata
 */
// TODO: refactor with context and move all resource-handling state and code in here?
const useLoadFormResources = (
  form,
  isDraft,
  draftMetadata,
  loading = false
) => {
  const dispatch = useDispatch();

  const { resourceVersions, resources: currentLatestResourceList } =
    useGlobalSelector();

  const resourcesNeeded = form?.dependentResources;
  const draftResourceVersions = useMemo(
    () => getDraftResourceVersions(draftMetadata),
    [draftMetadata]
  );

  /**
   * @type {[Boolean, Function]} Loading
   */
  const [anyNeedUpdate, setAnyNeedUpdate] = useState(false);
  /**
   * @type {[Map.<string, {oldVersion: string, latestVersion: string}>, Function]} Loading
   */
  const [resourceUpdatesNeeded, setResourceUpdatesNeeded] = useState();

  // Only need to do this if form has has loaded and has dependentResources,
  // and if/when dependentResources changes
  useDeepCompareEffectNoCheck(() => {
    if (!loading) {
      const draftReady = draftMetadata != null;
      if (!isDraft && resourcesNeeded) {
        // Fetch latest version of resources the form needs
        dispatch(fetchResources(resourcesNeeded));
      } else if (
        resourcesNeeded &&
        draftReady &&
        !isEqual(draftResourceVersions, resourceVersions)
      ) {
        // Initially, fetch resources form needs using the versions the draft was saved with, and use those versions for resource lookups
        const initializingFromDraft =
          areResourceVersionsEmpty(resourceVersions) &&
          !areResourceVersionsEmpty(draftResourceVersions);
        const resourceVersionsToUse = initializingFromDraft
          ? draftResourceVersions
          : resourceVersions;
        dispatch(fetchResources(resourcesNeeded, resourceVersionsToUse)); // if resource versions used changes, we need to fetch them into memory
        if (initializingFromDraft) {
          dispatch(setResourceVersions(resourceVersionsToUse));
        }
      }
    }

    if (!loading && Object.keys(form).length > 0 && !resourcesNeeded) {
      dispatch(setResourcesLoading(false));
    }
  }, [resourcesNeeded, draftResourceVersions, resourceVersions, loading]);

  useEffect(
    function getResourceVersionsToUpdate() {
      if (isDraft) {
        // updating resource versions manually only for drafts for now
        const safeResourcesNeeded = resourcesNeeded || [];
        let newAnyNeedUpdate = false;
        const newResourceUpdatesNeeded = new Map();

        safeResourcesNeeded.forEach((resourceIdentifierHash) => {
          const specifiedVersionUsed =
            resourceVersions?.[resourceIdentifierHash];
          const latestVersion = getLatestVersion(
            resourceIdentifierHash,
            currentLatestResourceList
          );
          const updateNeeded =
            specifiedVersionUsed != null &&
            parseInt(latestVersion, 0) > parseInt(specifiedVersionUsed, 0);
          if (!newAnyNeedUpdate && updateNeeded) {
            newAnyNeedUpdate = true;
          }
          if (updateNeeded) {
            newResourceUpdatesNeeded.set(resourceIdentifierHash, {
              latestVersion,
              oldVersion: specifiedVersionUsed,
            });
          }
        });

        setAnyNeedUpdate(newAnyNeedUpdate);
        setResourceUpdatesNeeded(newResourceUpdatesNeeded);
      }
    },
    [resourcesNeeded, resourceVersions, currentLatestResourceList, isDraft]
  );

  const getResourceVersionsToSaveDraft = useCallback(
    /**
     * @return {Array<{resource_identifier: string, version: string}>}
     */
    () => {
      const safeResourcesNeeded = resourcesNeeded || [];
      return safeResourcesNeeded
        .map((resourceIdentifierHash) => {
          const specifiedVersionUsed =
            resourceVersions?.[resourceIdentifierHash];
          const latestVersion = getLatestVersion(
            resourceIdentifierHash,
            currentLatestResourceList
          );
          const effectiveVersionUsed =
            specifiedVersionUsed != null ? specifiedVersionUsed : latestVersion;
          return effectiveVersionUsed != null
            ? {
                resource_identifier: resourceIdentifierHash,
                version: String(effectiveVersionUsed),
              }
            : undefined;
        })
        .filter((resourceVersionObj) => resourceVersionObj != null);
    },
    [resourcesNeeded, resourceVersions, currentLatestResourceList]
  );

  return {
    getResourceVersionsToSaveDraft,
    anyNeedUpdate,
    resourceUpdatesNeeded,
  };
};

/**
 * Get the versions of each required resource that the draft was saved with,
 * from the draftMetadata (list item)
 * @param draftMetadata - Draft list item/metadata
 * @return {Object.<string, number>} A map/dictionary with resource ID keys and version number values
 */
const getDraftResourceVersions = (draftMetadata) => {
  if (!draftMetadata) {
    return undefined;
  }
  const rawDraftResourceVersions = draftMetadata.resourceVersions;
  const draftResourceVersions = {};
  rawDraftResourceVersions.forEach((rawResourceVersion) => {
    const resourceIdentifierHash = rawResourceVersion.resource_identifier;
    const resourceVersion = rawResourceVersion.version;
    draftResourceVersions[resourceIdentifierHash] = resourceVersion;
  });
  return draftResourceVersions;
};

/**
 * @param {string} resourceIdentifierHash
 * @param {array} currentLatestResourceList
 * @returns {string}
 */
const getLatestVersion = (
  resourceIdentifierHash,
  currentLatestResourceList
) => {
  const latestFromResourceList = currentLatestResourceList.find(
    (resource) => resource.identifier === resourceIdentifierHash
  );
  const latestVersion = latestFromResourceList?.version;
  return latestVersion;
};

export default useLoadFormResources;
